import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

import { NgbModal } from '@ng-bootstrap/ng-bootstrap';

import { CheckInService } from '../../../../services/check-in/check-in.service';
import { CheckIn, CheckInStats, FailedCheckIn, InFacilityCheckIn } from '../../../../models/check-in';
import { AccountService } from '../../../../services/account/account.service';
import { timeStringFromISO8601 } from '../../../../utils';
import { CapitanLocation } from '../../../../models/location';
import { LocationService } from '../../../../services/location/location.service';
import { environment } from '../../../../../environments/environment';
import {
  CheckInDetailModalComponent
} from '../customers/customer-detail/sub-lists/check-ins-sub-list/modals/check-in-detail-modal/check-in-detail-modal.component';

enum TableChoice {
  inFacility,
  recentCheckIns,
  recentCheckOuts
}


@Component({
  selector: 'app-check-in',
  templateUrl: './check-in.component.html',
  styleUrls: ['./check-in.component.css']
})
export class CheckInComponent implements OnInit, OnDestroy {

  public location: CapitanLocation;
  public checkInStats: CheckInStats;
  public failedCheckIns: FailedCheckIn[];
  public inFacilityCheckIns: InFacilityCheckIn[];
  public recentCheckIns: CheckIn[];
  public recentCheckOuts: CheckIn[];

  public tableChoice: TableChoice = TableChoice.inFacility;

  public reloadSubject = new Subject<null>();
  public willRefreshCache = false;

  public loadingLocation = false;
  public loadingCheckInStats = false;
  public loadingFailedCheckIns = false;
  public loadingInFacilityCheckIns = false;
  public loadingRecentCheckIns = false;
  public loadingRecentCheckOuts = false;
  public loadingCheckOut = {};
  public checkOutComplete = {};

  public serverError: number = null;

  // Make relevant enums and library functions available to the template.
  public timeStringFromISO8601 = timeStringFromISO8601;
  public TableChoice = TableChoice;
  public environment = environment;

  private refreshIntervalHolder: any;

  constructor(
    public accountService: AccountService,
    private checkInService: CheckInService,
    private locationService: LocationService,
    private modalService: NgbModal,
  ) {}

  ngOnInit() {
    this.reloadAll(true, false);

    this.reloadSubject.pipe(
      // Avoid rapid requests by waiting 300ms.
      debounceTime(300)
    ).subscribe(() => {
      this.reloadAll(false, this.willRefreshCache);
      this.willRefreshCache = false;
    });

    // Refresh data every 60 seconds.
    this.refreshIntervalHolder = setInterval(
      () => {
        this.reloadSubject.next();
      },
      60 * 1000
    );
  }

  ngOnDestroy() {
    // Stop refreshing data when the component is destroyed.
    clearInterval(this.refreshIntervalHolder);
  }

  public locationIsOverCapacity() {
    if (this.loadingCheckInStats || this.loadingLocation) { return false; }
    if (!this.checkInStats || !this.location) { return false; }

    return (this.checkInStats.currently_checked_in_count > this.location.capacity);
  }

  public checkOut(checkInId: number) {
    this.loadingCheckOut[checkInId] = true;

    this.checkInService
      .checkOutCustomerViaCheckInId(checkInId)
      .then(() => {
        this.checkOutComplete[checkInId] = true;
        this.willRefreshCache = true;
        this.reloadSubject.next();
      })
      .catch(reason => {
        if (reason.status === 404) {
          // A 404 likely indicates that the in facility list is old - refresh it.
          this.willRefreshCache = true;
          this.reloadSubject.next();
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingCheckOut[checkInId] = false; });
  }

  public setTableChoice(tableChoice: TableChoice) {
    this.tableChoice = tableChoice;
    this.loadSelectedTableData(true, false);
  }

  public openCheckInDetailModal(checkInId: number) {
    const modalRef = this.modalService.open(CheckInDetailModalComponent);
    modalRef.componentInstance.checkInId = checkInId;
    modalRef.componentInstance.checkInEditedCallback = () => {
      this.loadSelectedTableData(false, true);
    };
  }

  private reloadAll(setLoadState: boolean, refreshCache: boolean) {
    this.loadLocation(setLoadState);
    this.loadCheckInStats(setLoadState, refreshCache);
    this.loadFailedCheckIns(setLoadState);
    this.loadSelectedTableData(setLoadState, refreshCache);
  }

  private loadSelectedTableData(setLoadState: boolean, refreshCache: boolean) {
    if (this.tableChoice === TableChoice.inFacility) {
      this.loadInFacilityCheckIns(setLoadState, refreshCache);
    } else if (this.tableChoice === TableChoice.recentCheckIns) {
      this.loadRecentCheckIns(setLoadState);
    } else if (this.tableChoice === TableChoice.recentCheckOuts) {
      this.loadRecentCheckOuts(setLoadState);
    }
  }

  private loadLocation(setLoadState: boolean) {
    if (setLoadState) {
      this.loadingLocation = true;
    }

    this.locationService
      .getLocation(this.accountService.getMyAccount().default_gym_id)
      .then(location => {
        this.location = location;
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingLocation = false; });
  }

  private loadCheckInStats(setLoadState: boolean,
                           refreshCache: boolean) {
    if (setLoadState) {
      this.loadingCheckInStats = true;
    }

    this.checkInService
      .getCheckInStats(
        this.accountService.getMyAccount().default_gym_id,
        refreshCache
      )
      .then(checkInStats => { this.checkInStats = checkInStats; })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingCheckInStats = false; });
  }

  private loadFailedCheckIns(setLoadState: boolean) {
    if (setLoadState) {
      this.loadingFailedCheckIns = true;
    }

    this.checkInService
      .getFailedCheckIns(this.accountService.getMyAccount().default_gym_id)
      .then(failedCheckIns => { this.failedCheckIns = failedCheckIns; })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingFailedCheckIns = false; });
  }

  private loadInFacilityCheckIns(setLoadState: boolean, refreshCache: boolean) {
    if (setLoadState) {
      this.loadingInFacilityCheckIns = true;
    }

    this.checkInService
      .getInFacilityCustomers(
        this.accountService.getMyAccount().default_gym_id,
        refreshCache
      )
      .then(inFacilityCheckIns => {
        this.inFacilityCheckIns = inFacilityCheckIns.results;
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingInFacilityCheckIns = false; });
  }

  private loadRecentCheckIns(setLoadState: boolean) {
    if (setLoadState) {
      this.loadingRecentCheckIns = true;
    }

    this.checkInService
      .getCheckIns(
        1,
        20,
        {
          this_calendar_day: true,
          has_checked_out: false,
          location_id: this.accountService.getMyAccount().default_gym_id
        },
        true
      )
      .then(recentCheckInsResult => {
        this.recentCheckIns = recentCheckInsResult.results;
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingRecentCheckIns = false; });
  }

  private loadRecentCheckOuts(setLoadState: boolean) {
    if (setLoadState) {
      this.loadingRecentCheckOuts = true;
    }

    this.checkInService
      .getCheckIns(
        1,
        20,
        {
          this_calendar_day: true,
          has_checked_out: true,
          location_id: this.accountService.getMyAccount().default_gym_id
        },
        true
      )
      .then(recentCheckOutsResult => {
        this.recentCheckOuts = recentCheckOutsResult.results;
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingRecentCheckOuts = false; });
  }

}
